import { createFileRoute, Link } from '@tanstack/react-router'
import { useEvent, useContentPages } from '@/lib/api/queries'
import { Input } from '@/components/ui/input'
import { useState } from 'react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { formatDate } from '@/lib/date'

export const Route = createFileRoute('/_authenticated/events/$eventId/event-content/')({
  component: EventContentPage,
})

function EventContentPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  
  // Permission checks
  const canViewEventContent = usePermission('can_view_event_content')
  const canEditEventContent = usePermission('can_edit_event_content')
  const canDeleteEventContent = usePermission('can_delete_event_content')
  void canDeleteEventContent // Future use
  
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  // setSuccessMessage is available for future use
  void setSuccessMessage
  
  const { data: event } = useEvent(eventIdNum)
  const { data, isLoading, error } = useContentPages(eventIdNum, {
    search: search || undefined,
    is_published: statusFilter === '' ? undefined : statusFilter === 'true',
    ordering: '-updated',
  })

  const contentPages = data?.results || []
  const totalCount = data?.count || 0
  
  // Permission guard
  if (!canViewEventContent) {
    return <AccessDenied message="You don't have permission to view event content." />
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // The search will be triggered by the query params change
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Event Content</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Manage content pages for {event?.name}
          </p>
        </div>
        
        {canEditEventContent && (
          <div className="mt-4 sm:mt-0">
            <Link
              to="/events/$eventId/event-content/create"
              params={{ eventId }}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Create Content Page
            </Link>
          </div>
        )}
      </div>

      {/* Success Messages */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
          <div className="flex">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <p className="text-sm text-green-800 dark:text-green-200">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 mb-6">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Search & Filters</h3>
        </div>
        <div className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Search content pages
              </label>
              <Input
                type="text"
                id="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title or content..."
                className="w-full"
              />
            </div>

            {/* Published Filter */}
            <div className="sm:w-48">
              <label htmlFor="is_published" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Status
              </label>
              <select
                id="is_published"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white"
              >
                <option value="">All</option>
                <option value="true">Published</option>
                <option value="false">Draft</option>
              </select>
            </div>

            {/* Submit */}
            <div className="flex items-end">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Content Pages List */}
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800">
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
            Content Pages ({totalCount})
          </h3>
        </div>

        {isLoading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="flex-1 h-4 bg-neutral-200 dark:bg-neutral-800 rounded"></div>
                  <div className="w-20 h-4 bg-neutral-200 dark:bg-neutral-800 rounded"></div>
                  <div className="w-32 h-4 bg-neutral-200 dark:bg-neutral-800 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600 dark:text-red-400">Failed to load content pages: {error.message}</p>
          </div>
        ) : contentPages.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Slug
                  </th>
                  {canEditEventContent && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {contentPages.map((page) => (
                  <tr key={page.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-neutral-900 dark:text-white">
                          {page.title}
                        </div>
                        {page.content && (
                          <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 truncate max-w-xs">
                            {stripHtml(page.content).slice(0, 60)}...
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {page.is_published ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                          <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></span>
                          Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400">
                          <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full mr-1.5"></span>
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400">
                      {formatDate(page.updated || page.created, 'MMM d, yyyy h:mm a')}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400 font-mono">
                      {page.slug || '-'}
                    </td>
                    {canEditEventContent && (
                      <td className="px-6 py-4 text-right">
                        <Link
                          to="/events/$eventId/event-content/$pageId/edit"
                          params={{ eventId, pageId: String(page.id) }}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Edit
                        </Link>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <svg className="w-12 h-12 text-neutral-400 dark:text-neutral-600 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">No content pages found</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">
              {search || statusFilter
                ? 'No content pages match your search criteria.'
                : 'Get started by creating your first content page for this event.'}
            </p>
            {canEditEventContent && !search && !statusFilter && (
              <Link
                to="/events/$eventId/event-content/create"
                params={{ eventId }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Create Content Page
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// Helper function to strip HTML tags
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}
